import { DecimalPipe, NgClass } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  Injector,
  OnDestroy,
  OnInit,
  Signal,
  signal,
  ViewChild,
  ViewEncapsulation,
  WritableSignal
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, ReactiveFormsModule, UntypedFormControl } from '@angular/forms';
import { MatButton, MatIconButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatMenu, MatMenuContent, MatMenuItem, MatMenuTrigger } from '@angular/material/menu';
import { MatPaginator } from '@angular/material/paginator';
import { MatProgressBar } from '@angular/material/progress-bar';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatDrawer, MatDrawerContainer, MatDrawerContent } from '@angular/material/sidenav';
import { ActivatedRoute, Params, Router, RouterOutlet } from '@angular/router';
import { PopupService } from '@app/core/services/popup.service';
import { PermissionDirective } from '@app/features/admin/settings/permission/directives/permission.directive';
import { PermissionService } from '@app/features/admin/settings/permission/permission.service';
import { SettingsState } from '@app/features/admin/settings/state/settings.state';
import { ProfileState } from '@app/layout/common/profile/state/profile.state';
import { TranslocoModule, TranslocoPipe, TranslocoService } from '@ngneat/transloco';
import {
  createAdvanceFilterConfig,
  ISharedAdvanceFilterConfig,
  SharedAdvanceFilterContainerComponent,
  SharedQueryAdvanceFilter
} from '@shared/components/filters';
import { TableComponent, TableConfig, TableTemplateDirective } from '@shared/components/table';
import { LIMIT_PER_PAGE_DEFAULT } from '@shared/shared.constants';
import { SafeAny } from '@shared/types/any.type';
import { SealFindByKeyPipe } from '@shipsfocus/seal';
import { TimesheetActions } from '@timesheet/state/timesheet.actions';
import { TimesheetService } from '@timesheet/state/timesheet.service';
import { TimesheetState } from '@timesheet/state/timesheet.state';
import { Timesheet, TimesheetPagination } from '@timesheet/state/timesheet.types';
import { AuthService } from 'app/core/auth/auth.service';
import { TableInterface } from 'app/core/interfaces/table.interface';
import { DatetimeLocalPipe } from 'app/core/pipe/datetime/datetime-local.pipe';
import { ExcelService } from 'app/core/services/excel.service';
import { RouterService } from 'app/core/services/router.service';
import { filterQueries } from 'app/core/utils/helpers';
import { from, merge, of, Subject, switchMap, take, takeUntil, tap } from 'rxjs';
import { TimesheetStatuses, TimesheetTypes } from '../timesheet.constants';

@Component({
  selector: 'timesheet-list',
  templateUrl: './list.component.html',
  styles: [
    `
      .timesheet-grid {
        grid-template-columns: repeat(9, 1fr) 3rem;

        &-menu {
          grid-template-columns: repeat(9, 1fr) 3rem;
        }
      }

      .mdc-list-item > span {
        width: auto !important;
      }
    `
  ],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    TranslocoPipe,
    NgClass,
    RouterOutlet,
    ReactiveFormsModule,
    TranslocoModule,
    MatButton,
    MatIconButton,
    MatIcon,
    MatMenu,
    MatMenuContent,
    MatMenuItem,
    MatMenuTrigger,
    MatPaginator,
    MatProgressBar,
    MatDrawer,
    MatDrawerContainer,
    MatDrawerContent,
    SharedAdvanceFilterContainerComponent,
    TableComponent,
    TableTemplateDirective,
    PermissionDirective,
    MatProgressSpinner
  ],
  providers: [DatetimeLocalPipe, DecimalPipe, SealFindByKeyPipe, RouterService]
})
export class TimesheetListComponent implements OnInit, AfterViewInit, OnDestroy {
  protected readonly translocoService: TranslocoService = inject(TranslocoService);
  protected readonly destroyRef: DestroyRef = inject(DestroyRef);
  protected readonly permissionService: PermissionService = inject(PermissionService);
  @ViewChild('detailsDrawer', { static: true }) detailsDrawer: MatDrawer;

  timesheets: Signal<Timesheet[]> = computed(() => this._timesheetState.timesheets());
  pagination: Signal<TimesheetPagination> = computed(() => this._timesheetState.pagination());
  selectedTimesheet: WritableSignal<Timesheet> = signal(void 0);
  drawerType: WritableSignal<'view' | 'search'> = signal(void 0);
  queryParams: WritableSignal<Params> = signal(void 0);
  isLoading: WritableSignal<boolean> = signal(false);

  selectedDateFilter: Signal<string | null> = computed(() => this._timesheetState.dateFilter());
  advanceFilterOwnerCompanies: Signal<SafeAny[]> = signal([]);
  advanceFilterVessels: Signal<SafeAny[]> = signal([]);
  protected readonly downloadingExcel: Signal<boolean> = this._excelService.listenToJob('TIMESHEET');

  timesheetTypes: typeof TimesheetTypes = TimesheetTypes;
  timesheetStatuses: typeof TimesheetStatuses = TimesheetStatuses;

  @ViewChild(MatPaginator) private _paginator: MatPaginator;

  protected readonly advanceFilterConfig: ISharedAdvanceFilterConfig = createAdvanceFilterConfig(
    [
      {
        key: 'type_id',
        filterKey: 'filter[type_id]',
        titleKey: 'ship_interface.type',
        selectionType: 'id',
        displayName: 'name',
        customClasses: 'hidden md:block',
        items: signal(TimesheetTypes.map((type) => ({ ...type, id: type.id.toString() })))
      },
      {
        key: 'date_timesheet',
        filterKey: 'filter[date_timesheet]',
        titleKey: 'ship_interface.date',
        customClasses: 'hidden md:block',
        menuType: 'date-range'
      },
      {
        key: 'voyage_number',
        filterKey: 'filter[voyage_number]',
        titleKey: 'ship_interface.voy',
        selectionType: 'voyage_number',
        displayName: 'voyage_number',
        customClasses: 'hidden md:block',
        items: computed(() => this.advanceFilterOwnerCompanies())
      },
      {
        key: 'next_port',
        filterKey: 'filter[port_id]',
        titleKey: 'ship_interface.port',
        selectionType: 'id',
        displayName: 'name',
        customClasses: 'hidden md:block',
        items: computed(() => this.advanceFilterVessels())
      },
      {
        key: 'status_id',
        filterKey: 'filter[status_id]',
        titleKey: 'ship_interface.status',
        selectionType: 'id',
        displayName: 'name',
        customClasses: 'hidden md:block',
        items: signal(TimesheetStatuses.map((status) => ({ ...status, id: status.id.toString() })))
      }
    ],
    this.queryParams
  );
  protected readonly searchInputControl: UntypedFormControl = this.advanceFilterConfig.search.control;

  protected readonly tableConfig: TableConfig<Timesheet> = {
    // template: [
    //   'lg:grid-cols-[repeat(9,1fr)_48px]',
    //   'md:grid-cols-[repeat(7,1fr)_48px]',
    //   'sm:grid-cols-[repeat(5,1fr)_48px]',
    //   'grid-cols-[repeat(3,1fr)_48px]'
    // ],
    controller: true,
    render: 'grid',
    header: {
      classes: 'sticky top-0 z-10 bg-white'
    },
    body: {
      byRow: (row) => ({
        classes: computed(
          () =>
            `hover:bg-slate-50 hover:cursor-pointer dark:hover:bg-hover ${this.selectedTimesheet()?.id === row.id ? 'bg-primary-100 dark:bg-primary-900' : ''}`
        )
      }),
      onClick: (row: Timesheet) => {
        this.selectedTimesheet.set(row);
        this._routerService.navigate(['./', row.id], {
          relativeTo: this._activatedRoute,
          queryParams: this.queryParams()
        });
      }
    },
    columns: [
      {
        key: 'date_timesheet',
        header: {
          value: 'ship_interface.date',
          classes: 'hidden sm:block'
        },
        body: {
          value: (row: Timesheet) => (row.date_timesheet ? this._dateTimeLocalPipe.transform(row.date_timesheet, { format: 'DD MMM YYYY' }) : '-'),
          classes: 'truncate hidden sm:block'
        },
        controller: {
          label: 'ship_interface.date',
          template: {
            sm: '1fr'
          }
        }
      },
      {
        key: 'type_name',
        header: {
          value: 'ship_interface.timesheet_type'
        },
        body: {
          value: (row) => row.type_name,
          wrapper: {
            by: 'type'
          }
        },
        controller: {
          label: 'ship_interface.timesheet_type',
          disabled: true,
          template: {
            default: '1fr'
          }
        }
      },
      {
        key: 'voyage_number',
        header: {
          value: 'ship_interface.voyage_number'
        },
        body: {
          value: (row: Timesheet) => row.voyage_number || '-',
          classes: 'truncate'
        },
        controller: {
          label: 'ship_interface.voyage_number',
          disabled: true,
          template: {
            default: '1fr'
          }
        }
      },
      {
        key: 'port_name',
        header: {
          value: 'ship_interface.port'
        },
        body: {
          value: (row: Timesheet) => {
            const port = row.port_name || '';
            const country = row.country_name || '';
            return country ? `${port}, ${country}` : port || '-';
          },
          classes: 'truncate'
        },
        controller: {
          label: 'ship_interface.port',
          disabled: true,
          template: {
            default: '1fr'
          }
        }
      },
      {
        key: 'location_name',
        header: {
          value: 'ship_interface.location',
          classes: 'hidden md:block'
        },
        body: {
          value: (row: Timesheet) => row.location_name || '-',
          classes: 'truncate hidden md:block'
        },
        controller: {
          label: 'ship_interface.location',
          template: {
            md: '1fr'
          }
        }
      },
      {
        key: 'cargo_name',
        header: {
          value: 'ship_interface.cargo_bunker_name',
          classes: 'hidden md:block'
        },
        body: {
          value: (row: Timesheet) => row.cargo_name || '-',
          classes: 'truncate hidden md:block'
        },
        controller: {
          label: 'ship_interface.cargo_bunker_name',
          template: {
            md: '1fr'
          }
        }
      },
      {
        key: 'quantity_document',
        header: {
          value: 'ship_interface.qty_document',
          classes: 'hidden lg:block'
        },
        body: {
          value: (row: Timesheet) => (row.quantity_document ? `${this._decimalPipe.transform(row.quantity_document, '1.3-3')} MT` : '-'),
          classes: 'truncate hidden lg:block'
        },
        controller: {
          label: 'ship_interface.qty_document',
          template: {
            lg: '1fr'
          }
        }
      },
      {
        key: 'master_name',
        header: {
          value: 'ship_interface.master',
          classes: 'hidden lg:block'
        },
        body: {
          value: (row: Timesheet) => row.master_name || '-',
          classes: 'truncate hidden lg:block'
        },
        controller: {
          label: 'ship_interface.master',
          template: {
            lg: '1fr'
          }
        }
      },
      {
        key: 'status_id',
        header: {
          value: 'ship_interface.status',
          classes: 'hidden sm:block'
        },
        body: {
          value: (row) => row.status_name,
          wrapper: {
            by: 'status',
            type: (row) => this._sealFindByKey.transform(row.status_id, 'id', TimesheetStatuses)?.type
          },
          classes: 'hidden sm:block'
        },
        controller: {
          label: 'ship_interface.status',
          template: {
            sm: '1fr'
          }
        }
      },
      {
        key: 'actions',
        header: {
          hidden: computed(() => !this.permissionService.checkPermission(['time_sheet_sof', ['delete']]))
        },
        body: computed(() => {
          if (this.permissionService.checkPermission(['time_sheet_sof', ['delete']])) {
            return {
              by: 'template',
              value: 'actions',
              classes: ''
            };
          }

          return { value: () => '' };
        }),
        controller: {
          admin: true,
          template: '48px'
        }
      }
    ]
  };

  protected readonly queryAdvanceFilter: SharedQueryAdvanceFilter = (key: string) => {
    if (key === 'type_id') {
      return of(TimesheetTypes.map((type) => ({ ...type, id: type.id.toString() })));
    }

    if (key === 'status_id') {
      return of(TimesheetStatuses.map((status) => ({ ...status, id: status.id.toString() })));
    }

    return of([]);
  };

  private _unsubscribeAll: Subject<any> = new Subject<any>();

  constructor(
    private _router: Router,
    private _activatedRoute: ActivatedRoute,
    private _injector: Injector,
    private _changeDetectorRef: ChangeDetectorRef,
    private _formBuilder: FormBuilder,
    private _authService: AuthService,
    private _timesheetState: TimesheetState,
    private _timesheetActions: TimesheetActions,
    private _timesheetService: TimesheetService,
    private _settingsState: SettingsState,
    private _popupService: PopupService,
    private _dateTimeLocalPipe: DatetimeLocalPipe,
    private _decimalPipe: DecimalPipe,
    private _sealFindByKey: SealFindByKeyPipe,
    private _profileState: ProfileState,
    private _excelService: ExcelService,
    private _routerService: RouterService
  ) {}

  ngOnInit(): void {
    effect(
      () => {
        this.selectedTimesheet.set({ ...this._timesheetState.timesheet() });

        if (this._timesheetState.timesheet()) {
          this.drawerType.set('view');
        }
      },
      { injector: this._injector }
    );

    this._activatedRoute.queryParams.pipe(takeUntil(this._unsubscribeAll)).subscribe((queryParams) => {
      this.queryParams.set({ ...queryParams });
    });

    this.detailsDrawer.openedChange.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((opened) => {
      if (!opened) {
        this.selectedTimesheet.set(null);
        this.drawerType.set(null);
      }
    });
  }

  ngAfterViewInit(): void {
    merge(this._paginator.page)
      .pipe(
        switchMap(() => {
          this.isLoading.set(true);

          const queryParams = {
            // 'filter[search]'         : this.searchInputControl.value ?? undefined,
          };

          return this._router.navigate(['./'], {
            relativeTo: this._activatedRoute,
            queryParams: {
              page: this._paginator.pageIndex + 1,
              limit: this._paginator.pageSize,
              ...queryParams
            }
          });
        }),
        tap(() => {
          this.isLoading.set(false);
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => {
        this._timesheetActions.actions.getTimesheets({ queryParams: this.queryParams() });
      });
  }

  ngOnDestroy(): void {
    this._unsubscribeAll.next(null);
    this._unsubscribeAll.complete();
  }

  backdropClicked(): void {
    if (this.drawerType() === 'view') {
      this._router.navigate(['./'], { relativeTo: this._activatedRoute, queryParams: this.queryParams() }).then();
    }

    if (this.drawerType() === 'search') {
      this.detailsDrawer?.close()?.then();
    }

    this._changeDetectorRef.markForCheck();
  }

  selectTimesheet(): void {
    this.drawerType.set('view');
  }

  createTimesheet(): void {
    this.drawerType.set('view');

    this._router.navigate(['./', 'new'], { relativeTo: this._activatedRoute, queryParams: this.queryParams() }).then();
  }

  deleteTimesheet(timesheet: Timesheet): void {
    this._popupService.openConfirmationDialog({
      title: this.translocoService.translate('ship_interface.delete_timesheet'),
      message: this.translocoService.translate('ship_interface.are_you_sure_you_want_to_delete_this_timesheet_this_action_cannot_be_undone'),
      actionType: 'delete',
      confirmButtonColor: 'warn',
      dismissible: true,
      onAccept: () => {
        this._timesheetActions.actions.deleteTimesheet({ id: timesheet.id, activatedRoute: this._activatedRoute });
      },
      onCancel: () => {}
    });
  }

  exportTimesheetsToExcel(): void {
    const queryParams = filterQueries(this.queryParams());
    this._timesheetService
      .getExcelTimesheets(queryParams)
      .pipe(
        take(1),
        switchMap((response: Timesheet[]) =>
          from(
            this._excelService.generateExcel(
              [
                {
                  title: this.translocoService.translate('ship_interface.timesheets'),
                  tabColor: '#4FC3F7',
                  header: {
                    font: { size: 12, bold: true, color: '#FFFFFF' },
                    alignment: { horizontal: 'center' },
                    fill: { pattern: 'solid', color: '#3949AB' }
                  },
                  table: {
                    config: this.TimesheetTableConfig,
                    data: response?.map((timesheet: Timesheet) => ({
                      ...timesheet,
                      date_timesheet: timesheet?.date_timesheet ? this._dateTimeLocalPipe.transform(timesheet?.date_timesheet, { format: 'DD MMM YYYY' }) : '-',
                      port_name: `${timesheet?.port_name || '-'}, ${timesheet?.country_name || '-'}`,
                      quantity_document: `${this._decimalPipe.transform(timesheet?.quantity_document, '1.3-3')} MT`
                    }))
                  },
                  uppercase: true
                }
              ],
              `Timesheets`
            )
          )
        ),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();
  }

  trackByFn(index: number, item: any): any {
    return item.id || index;
  }

  navigateWithParams(additionalParams: object = {}): void {
    this._router
      .navigate(['./'], {
        relativeTo: this._activatedRoute,
        queryParams: {
          page: '1',
          limit: this.queryParams()?.['limit'] || LIMIT_PER_PAGE_DEFAULT,
          sort: this.queryParams()?.['sort'] || '',
          ...additionalParams
        }
      })
      .then(() => {
        this._timesheetActions.actions.getTimesheets({
          queryParams: this.queryParams()
        });
      });
  }

  private TimesheetTableConfig: TableInterface[] = [
    {
      width: 1,
      key: 'date_timesheet',
      header: 'ship_interface.date',
      filter: { type: 'text' },
      style: { textFmt: {}, border: { direction: 'all', style: 'thin', color: 'FFE0E0E0' } }
    },
    {
      width: 1,
      key: 'type_name',
      header: 'ship_interface.timesheet_type',
      filter: { type: 'text' },
      style: { textFmt: {}, border: { direction: 'all', style: 'thin', color: 'FFE0E0E0' } }
    },
    {
      width: 1,
      key: 'voyage_number',
      header: 'ship_interface.voyage',
      filter: { type: 'text' },
      style: { textFmt: {}, border: { direction: 'all', style: 'thin', color: 'FFE0E0E0' } }
    },
    {
      width: 1,
      key: 'port_name',
      header: 'ship_interface.port',
      filter: { type: 'text' },
      style: { textFmt: {}, border: { direction: 'all', style: 'thin', color: 'FFE0E0E0' } }
    },
    {
      width: 1,
      key: 'location_name',
      header: 'ship_interface.location',
      filter: { type: 'text' },
      style: { textFmt: {}, border: { direction: 'all', style: 'thin', color: 'FFE0E0E0' } }
    },
    {
      width: 1,
      key: 'cargo_name',
      header: 'ship_interface.cargo_bunker_name',
      filter: { type: 'text' },
      style: { textFmt: {}, border: { direction: 'all', style: 'thin', color: 'FFE0E0E0' } }
    },
    {
      width: 1,
      key: 'quantity_document',
      header: 'ship_interface.qty_document',
      filter: { type: 'text' },
      style: { textFmt: {}, border: { direction: 'all', style: 'thin', color: 'FFE0E0E0' } }
    },
    {
      width: 1,
      key: 'master_name',
      header: 'ship_interface.master',
      filter: { type: 'text' },
      style: { textFmt: {}, border: { direction: 'all', style: 'thin', color: 'FFE0E0E0' } }
    },
    {
      width: 1,
      key: 'status_name',
      header: 'ship_interface.status',
      filter: { type: 'text' },
      style: { textFmt: {}, border: { direction: 'all', style: 'thin', color: 'FFE0E0E0' } }
    }
  ];

  protected exportJobExcel(): void {
    if (this.downloadingExcel()) {
      return;
    }
    this._excelService.triggerJobFlowAsync('TIMESHEET', this.queryParams());
  }
}
